import React from "react";
import { AppBar, Toolbar, Typography, Button, Box, Avatar, Menu, MenuItem, IconButton, Badge, Divider, Chip } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import MenuIcon from "@mui/icons-material/Menu";
import { useState } from "react";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ImageIcon from "@mui/icons-material/Image";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DoneIcon from "@mui/icons-material/Done";
// Notifications are fetched from backend API to avoid Firestore client permissions issues

export default function Header() {
  const { user, role, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestNotifs, setLatestNotifs] = useState([]);
  const [mailUnread, setMailUnread] = useState(0);
  const navigate = useNavigate();

  // Profile menu handlers
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleOpenNotifs = (e) => setNotifAnchorEl(e.currentTarget);
  const handleCloseNotifs = () => setNotifAnchorEl(null);

  // Poll backend for admin notifications (unread count + latest list)
  React.useEffect(() => {
    let timer;
    const fetchData = async () => {
      try {
        if (!user) return;
        const token = await user.getIdToken();
        const base = (process.env.REACT_APP_BACKEND_URL || '');
        // Admin notifications
        if (role === 'admin') {
          const resp = await fetch(`${base}/api/notify`, { headers: { Authorization: `Bearer ${token}` } });
          if (resp.ok) {
            const data = await resp.json();
            setUnreadCount(data.unreadCount || 0);
            setLatestNotifs(Array.isArray(data.latest) ? data.latest : []);
          }
        }
        // Mail unread count
        const mresp = await fetch(`${base}/api/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        if (mresp.ok) {
          const j = await mresp.json();
          setMailUnread(j.unread || 0);
        }
      } catch (_) {
        // ignore transient errors
      }
    };
    fetchData();
    timer = setInterval(fetchData, 20000); // 20s poll
    return () => { if (timer) clearInterval(timer); };
  }, [user, role]);

  const markAsRead = async (id) => {
    try {
      if (!user) return;
      // optimistic update
      setLatestNotifs((prev) => prev.map(n => n.id === id ? { ...n, unread: false } : n));
      setUnreadCount((c) => (c > 0 ? c - 1 : 0));
      const token = await user.getIdToken();
      const base = (process.env.REACT_APP_BACKEND_URL || '');
      await fetch(`${base}/api/notify/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // ignore
    }
  };

  const markAllAsRead = async () => {
    const unread = latestNotifs.filter(n => n.unread).map(n => n.id);
    if (unread.length === 0) return;
    try {
      // optimistic update
      setLatestNotifs((prev) => prev.map(n => ({ ...n, unread: false })));
      setUnreadCount(0);
      for (const id of unread) {
        try { await markAsRead(id); } catch (_) { /* ignore per-item */ }
      }
    } catch (_) { /* noop */ }
  };

  const routeForNotification = (n) => {
    switch (n.type) {
      case 'user_registered':
      case 'profile_picture_submitted':
        return '/admin/users';
      case 'event_signup': {
        const evId = n?.payload?.eventId || n?.eventId;
        return evId ? `/admin/event/${evId}` : '/admin';
      }
      default:
        return null;
    }
  };

  const toDate = (v) => {
    if (!v) return null;
    if (typeof v === 'string' || typeof v === 'number') return new Date(v);
    if (v.seconds) return new Date(v.seconds * 1000);
    if (v._seconds) return new Date(v._seconds * 1000);
    if (typeof v.toDate === 'function') return v.toDate();
    return null;
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Date.now() - d.getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    return days === 1 ? '1d ago' : `${days}d ago`;
  };

  const iconForType = (type) => {
    switch (type) {
      case 'user_registered': return <PersonAddAlt1Icon fontSize="small" />;
      case 'profile_picture_submitted': return <ImageIcon fontSize="small" />;
      case 'event_signup': return <EventAvailableIcon fontSize="small" />;
      default: return <NotificationsIcon fontSize="small" />;
    }
  };

  const handleNotifClick = async (n) => {
    handleCloseNotifs();
    await markAsRead(n.id);
    const route = routeForNotification(n);
    if (route) navigate(route);
  };

  // Links for different user roles
  const guestLinks = [
    { label: "Home", to: "/" },
    { label: "Login", to: "/login" },
    { label: "Register", to: "/register" },
  ];

  const userLinks = [
    { label: "Home", to: "/" },
    { label: "My Profile", to: "/profile" },
    { label: "Logout", action: async () => { await logout(); navigate("/login"); } },
  ];

  const adminLinks = [
    { label: "Home", to: "/" },
    { label: "Admin Dashboard", to: "/admin" },
    { label: "My Profile", to: "/profile" },
    { label: "Logout", action: async () => { await logout(); navigate("/login"); } },
  ];

  let links;
  if (!user) {
    links = guestLinks;
  } else if (role === "admin") {
    links = adminLinks;
  } else {
    links = userLinks;
  }

  return (
    <AppBar position="static" color="primary" elevation={1}>
      <Toolbar>
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{
            flexGrow: 1,
            textDecoration: "none",
            color: "inherit",
            fontWeight: 700,
            letterSpacing: 1.5,
          }}
        >
          Christian Speed Dating
        </Typography>
        {/* Desktop links */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, alignItems: 'center' }}>
          {user && (
            <IconButton color="inherit" onClick={() => navigate('/messages')} sx={{ mr: 1 }} aria-label="Messages">
              <Badge color="error" badgeContent={mailUnread || 0} invisible={!mailUnread} overlap="circular">
                <MailOutlineIcon />
              </Badge>
            </IconButton>
          )}
          {user && role === 'admin' && (
            <IconButton color="inherit" onClick={handleOpenNotifs} sx={{ mr: 1 }} aria-label="Notifications">
              <Badge color="error" badgeContent={unreadCount || 0} invisible={!unreadCount} overlap="circular">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
          {links.map((link, i) =>
            link.action ? (
              <Button
                key={i}
                color="inherit"
                onClick={link.action}
                sx={{ textTransform: "none" }}
              >
                {link.label}
              </Button>
            ) : (
              <Button
                key={i}
                color="inherit"
                component={RouterLink}
                to={link.to}
                sx={{ textTransform: "none" }}
              >
                {link.label}
              </Button>
            )
          )}
        </Box>
        {/* Mobile hamburger menu */}
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: 'center' }}>
          {user && (
            <IconButton color="inherit" onClick={() => navigate('/messages')} aria-label="Messages" sx={{ mr: 0.5 }}>
              <Badge color="error" badgeContent={mailUnread || 0} invisible={!mailUnread} overlap="circular">
                <MailOutlineIcon />
              </Badge>
            </IconButton>
          )}
          {user && role === 'admin' && (
            <IconButton color="inherit" onClick={handleOpenNotifs} aria-label="Notifications" sx={{ mr: 0.5 }}>
              <Badge color="error" badgeContent={unreadCount || 0} invisible={!unreadCount} overlap="circular">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
          <IconButton
            color="inherit"
            edge="end"
            onClick={handleMenu}
            sx={{ ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{ sx: { minWidth: 160 } }}
          >
            {links.map((link, i) =>
              link.action ? (
                <MenuItem
                  key={i}
                  onClick={async () => {
                    await link.action();
                    handleClose();
                  }}
                >
                  {link.label}
                </MenuItem>
              ) : (
                <MenuItem
                  key={i}
                  component={RouterLink}
                  to={link.to}
                  onClick={handleClose}
                >
                  {link.label}
                </MenuItem>
              )
            )}
          </Menu>
          {/* Mobile menu remains; notifications Menu is shared below */}
        </Box>
        {/* Shared Notifications Menu for both desktop and mobile */}
        {user && role === 'admin' && (
          <Menu
            anchorEl={notifAnchorEl}
            open={Boolean(notifAnchorEl)}
            onClose={handleCloseNotifs}
            PaperProps={{ sx: { width: 360, maxWidth: '92vw' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Notifications</Typography>
              <Button size="small" onClick={markAllAsRead} disabled={!unreadCount}>Mark all read</Button>
            </Box>
            <Divider />
            {latestNotifs.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2">No notifications</Typography>
              </MenuItem>
            )}
            {latestNotifs.map((n) => {
              const dt = toDate(n.createdAt);
              return (
                <MenuItem
                  key={n.id}
                  onClick={() => handleNotifClick(n)}
                  sx={{
                    alignItems: 'flex-start',
                    whiteSpace: 'normal',
                    gap: 1.5,
                    bgcolor: n.unread ? 'action.hover' : 'transparent'
                  }}
                >
                  <Box sx={{ mt: 0.2 }}>{iconForType(n.type)}</Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: n.unread ? 700 : 500, flexGrow: 1 }}>
                        {n.title || 'Notification'}
                      </Typography>
                      {n.unread && <Chip size="small" color="primary" label="New" />}
                    </Box>
                    {n.message && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{n.message}</Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">{timeAgo(dt)}</Typography>
                  </Box>
                  <IconButton size="small" edge="end" onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }} aria-label="Mark as read">
                    <DoneIcon fontSize="small" />
                  </IconButton>
                </MenuItem>
              );
            })}
          </Menu>
        )}
        {/* User avatar (optional) */}
        {user && user.photoURL && (
          <Avatar
            src={user.photoURL}
            alt={user.displayName || ""}
            sx={{ ml: 2, display: { xs: "none", md: "flex" } }}
          />
        )}
      </Toolbar>
    </AppBar>
  );
}